import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { IconSquare } from '@tabler/icons-react';

import { Button, Tabs, Tooltip } from '@zendesk/forethought-elements';
import AbTestConfigPage from './ab-test-config/AbTestConfigPage';
import { useDebouncedUpdateAbTestConfig } from './ab-test-config/hooks';
import { getAreAllSectionsComplete } from './ab-test-config/utils';
import AbTestResultsPage from './ab-test-results/AbTestResultsPage';
import AbTestConversationsTab from './conversations/AbTestConversationsTab';
import AbTestMoreMenu from './AbTestMoreMenu';
import Banner from './Banner';
import StopAbTestConfirmModal from './StopAbTestConfirmModal';
import VersionStalenessModal from './VersionStalenessModal';
import NavbarV2 from 'src/components/app/navbar/navbarV2';
import { useStateParams } from 'src/hooks/hooks';
import { useAbTestConfigurationChangeEventSubscription } from 'src/hooks/PusherEventHooks';
import WorkflowConflictMessage from 'src/pages/workflow-builder-edit/WorkflowConflictMessage';
import {
  useGetAbTestConfigByIdQuery,
  useStartAbTestConfigMutation,
  useValidateAbTestConfigMutation,
} from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfig,
  VersionFreshnessIssue,
} from 'src/services/agent-ab-test/types';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { isForethoughtError } from 'src/slices/workflow-preview/types';
import { useAppDispatch } from 'src/store/hooks';
import { PRODUCT_TO_CHANNEL_MAP } from 'src/utils/constants';
import { Routes } from 'src/utils/enums';
import { getUserOrgCode } from 'src/utils/getUserOrgCode';
import { initializePusher } from 'src/utils/pusherUtils';

const TAB_VIEWS = ['setup', 'results', 'conversations'] as const;
type TabView = (typeof TAB_VIEWS)[number];

const AgentAbTestDetailPage = () => {
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const { t } = useTranslation('agent-ab-test');
  const { flushPendingSave, isLoading: isUpdating } =
    useDebouncedUpdateAbTestConfig();
  const navigate = useNavigate();

  const { abTestId = '' } = useParams();

  const { data, refetch } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const [hasConflict, setHasConflict] = useState(false);
  useEffect(() => {
    initializePusher();
  }, []);
  useAbTestConfigurationChangeEventSubscription({
    abTestConfigId: abTestId,
    onEvent: useCallback(() => setHasConflict(true), []),
    orgCode: getUserOrgCode(),
  });
  const [startAbTest, { isLoading: isStarting }] =
    useStartAbTestConfigMutation();
  const [validateAbTestConfig, { isLoading: isValidating }] =
    useValidateAbTestConfigMutation();

  const [stoppingAbTestConfig, setStoppingAbTestConfig] =
    useState<SolveABTestConfig>();
  const cancelStop = useCallback(() => {
    setStoppingAbTestConfig(undefined);
  }, []);

  const [freshnessIssues, setFreshnessIssues] = useState<
    VersionFreshnessIssue[]
  >([]);
  const [showStalenessModal, setShowStalenessModal] = useState(false);

  const [view, setView] = useStateParams<TabView>({
    deserialize: param => param as TabView,
    initialState: 'setup',
    paramsName: 'tab',
    serialize: param => param,
  });

  const tabIndex = TAB_VIEWS.indexOf(view);

  const tabs = {
    tabComponents: [
      t('detailPage.tabSetup'),
      ...(!data?.status || data?.status === 'draft'
        ? []
        : [t('detailPage.tabResults'), t('detailPage.tabConversations')]),
    ],
  };

  const tabsToComponentMap: Record<TabView, React.ReactNode> = {
    conversations: data?.variants ? (
      <AbTestConversationsTab
        abTestId={abTestId}
        channel={
          data.product ? PRODUCT_TO_CHANNEL_MAP[data.product] : undefined
        }
        endDatetime={data.end_condition?.end_datetime ?? undefined}
        intentWorkflowId={data.intent_workflow_id ?? ''}
        startDatetime={data.start_datetime ?? undefined}
        timezone={data.timezone ?? undefined}
        variants={data.variants}
      />
    ) : null,
    results: <AbTestResultsPage />,
    setup: <AbTestConfigPage />,
  };

  return (
    <Box
      sx={{
        bgcolor: palette.colors.white,
        height: `calc(100% - ${data?.status === 'draft' ? '114px' : '154px'})`,
      }}
    >
      <WorkflowConflictMessage
        buttonMessage={t('detailPage.conflictReloadButton')}
        message={t('detailPage.conflictMessage')}
        onButtonClick={() => {
          refetch();
          setHasConflict(false);
        }}
        open={hasConflict}
        title={t('detailPage.conflictTitle')}
      />
      {stoppingAbTestConfig && (
        <StopAbTestConfirmModal
          abTestConfig={stoppingAbTestConfig}
          onClose={cancelStop}
        />
      )}
      {showStalenessModal && data && (
        <VersionStalenessModal
          abTestConfig={data}
          freshnessIssues={freshnessIssues}
          onClose={() => {
            setShowStalenessModal(false);
            setFreshnessIssues([]);
            refetch();
          }}
          open={showStalenessModal}
        />
      )}
      {data && <Banner abTest={data} />}
      <NavbarV2
        currentTabOverride={
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '16px',
            }}
          >
            {data?.name}
            {data && <AbTestMoreMenu abTest={data} navigateToListOnDelete />}
          </Box>
        }
        onGoBack={() => navigate(Routes.AGENT_AB_TESTS)}
        top={data?.status === 'draft' ? undefined : '40px'}
      ></NavbarV2>
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: palette.background.default,
          borderBottom: `1px solid ${palette.colors.slate[200]}`,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 40px',
          position: 'sticky',
          top: data?.status === 'draft' ? '65px' : '105px',
          zIndex: 2,
        }}
      >
        <Tabs
          a11yProps={index => ({
            'aria-controls': `tabpanel-${index}`,
            id: `ab-test-detail-tab-${index}`,
          })}
          aria-label={t('detailPage.tabAriaLabel')}
          onChange={(_, index) => setView(TAB_VIEWS[index])}
          tabs={tabs.tabComponents}
          typographyVariant='font16Bold'
          value={tabIndex}
        />
        {view === 'setup' && data?.status === 'draft' && (
          <Tooltip
            tooltipContent={
              !(data && getAreAllSectionsComplete(data)) &&
              t('detailPage.runTooltip')
            }
          >
            <Button
              disabled={!(data && getAreAllSectionsComplete(data))}
              isLoading={isUpdating || isValidating || isStarting}
              onClick={async () => {
                if (!data) {
                  return;
                }

                try {
                  // Flush any pending debounced save before validating
                  flushPendingSave();

                  // Step 1: Server-side validation
                  const validation = await validateAbTestConfig(
                    data.ab_test_config_id,
                  ).unwrap();

                  if (!validation.is_valid) {
                    dispatch(
                      setGlobalToastOptions({
                        autoHideDuration: 8000,
                        title: t('detailPage.toastCannotStart', {
                          errors: validation.validation_errors.join('; '),
                        }),
                        variant: 'danger',
                      }),
                    );
                    return;
                  }

                  // Step 2: Check for version freshness issues
                  if (validation.version_freshness_issues?.length) {
                    setFreshnessIssues(validation.version_freshness_issues);
                    setShowStalenessModal(true);
                    return;
                  }

                  // Step 3: Start the test
                  await startAbTest(data.ab_test_config_id).unwrap();
                } catch (error) {
                  dispatch(
                    setGlobalToastOptions({
                      autoHideDuration: 5000,
                      title:
                        typeof error === 'object' &&
                        error !== null &&
                        'data' in error &&
                        isForethoughtError(error.data)
                          ? error.data.message
                          : t('detailPage.toastStartFailed'),
                      variant: 'danger',
                    }),
                  );
                }
              }}
            >
              {t('detailPage.runButton')}
            </Button>
          </Tooltip>
        )}
        {view === 'setup' && data?.status === 'running' && (
          <Button
            onClick={() => {
              setStoppingAbTestConfig(data);
            }}
            startIcon={<IconSquare size={16} />}
            variant='danger'
          >
            {t('detailPage.stopButton')}
          </Button>
        )}
      </Box>
      {tabsToComponentMap[view]}
    </Box>
  );
};
export default AgentAbTestDetailPage;
